<script>
  import { Link, useLocation, useMatch } from "svelte-navigator";
  import AppSideNavItem from "./AppSideNavItem.svelte";
  import { ROUTES } from "@utils/routes.js";
  import { Session } from "svelte-session-manager";
  import { userStores } from "@stores/index.js";

  //   carbon icons
  import Settings16 from "carbon-icons-svelte/lib/Settings16";
  import Folder16 from "carbon-icons-svelte/lib/Folder16";
  import List16 from "carbon-icons-svelte/lib/List16";
  import ChartLine16 from "carbon-icons-svelte/lib/ChartLine16";
  import UserIcon from "./icons/UserIcon.svelte";
  import SideNavLogo from "./icons/SideNavLogo.svelte";
  import SideNavHeaderLogo from "./SideNavHeaderLogo.svelte";
  import { getContext } from "svelte";
  import { fade, fly } from "svelte/transition";
  import PermissionsIcon from "./icons/PermissionsIcon.svelte";
  import TeamIcon from "./icons/TeamIcon.svelte";
  import DataIcon from "./icons/DataIcon.svelte";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";
  import AccountMenu from "./AccountMenu.svelte";
  import CompanySettingsMenu from "./CompanySettingsMenu.svelte";
  import PaxbotChat from "lib/components/paxbot/PaxbotChat.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { paxbotHelpContext } from "lib/stores/paxbotHelpStore.js";
  import { viewTour } from "lib/stores/viewTourStore.js";

  let expanded = false;
  let closingNavigation = false;
  const location = useLocation();

  const session = getContext("session");
  const sessionUpdate = getContext("sessionUpdate");

  //   const icons = {
  //   user: UserIcon,
  //   permissions: PermissionsIcon,
  //   team: TeamIcon,
  //   data: DataIcon
  // };

  const damRouteMatcher = useMatch(ROUTES.dam.all);
  const damAnalysisRouteMatcher = useMatch(ROUTES.dam.analysis.all);
  const knexRouteMatcher = useMatch(ROUTES.knex.all);
  const processRegistrationMatcher = useMatch(ROUTES.processRegistration.all);
  const anonymizationCheckMatcher = useMatch(
    ROUTES.processRegistration.anonymizationCheck,
  );
  const preprocessingMatcher = useMatch(ROUTES.preprocessing.all);
  const analysisRouteMatcher = useMatch(ROUTES.analysis.all);
  const potentialScopeMatcher = useMatch(ROUTES.knex.potentialScope.all);
  const potentialDashboardMatcher = useMatch(
    ROUTES.knex.potentialScope.dashboard,
  );
  const manageInvitationsMatcher = useMatch(
    ROUTES.knex.potentialScope.manageInvitations,
  );
  const surveyRouteMatcher = useMatch(ROUTES.knex.survey);
  const settingsRouteMatcher = useMatch(ROUTES.settings.all);
  const monitoringRouteMatcher = useMatch(ROUTES.monitoring.all);
  const processMonitoringRouteMatcher = useMatch(ROUTES.monitoring.processAll);

  const role = getContext("role");
  // const userStores = getContext("user");

  let permissions = [];
  $: setPermissions($userStores?.groups);
  function setPermissions(groups) {
    permissions = new Set();
    groups
      ?.map((group) => group.permissions)
      .forEach((perms) => {
        perms?.forEach((p) => {if(p != "enable_pwd")permissions.add(p)});
      });
    permissions = Array.from(permissions);
  }

  $: currentPath = ($location?.pathname || ROUTES.home).replace(/\/+$/, "") || ROUTES.home;
  $: isHome = currentPath === ROUTES.home || currentPath === ROUTES.dam.root;
  $: isCompanySettings = currentPath.startsWith(ROUTES.dam.companySettings.root);
  $: showCompanySettings =
    $uiFeatureStore.mainAreas.companySettings &&
    (isHome || isCompanySettings || currentPath === ROUTES.dam.processPresets);
  $: projectId = new URLSearchParams($location?.search || "").get("idproject");
  $: hasProject = !!projectId;
  // Home always opens the default landing screen without project context.
  $: homeHref = ROUTES.home;
  $: keepHomeParams = false;
  $: logoHref = hasProject
    ? `${ROUTES.dam.progress}?idproject=${encodeURIComponent(projectId)}`
    : ROUTES.dam.root;
  $: accountName =
    $userStores?.username || localStorage.getItem("username") || "User";
  $: accountEmail = $userStores?.email || "";

  let paxbotOpen = false;

  $: activeHelpView = $paxbotHelpContext;
  $: helpContext = {
    page: activeHelpView.page,
    view: activeHelpView.title,
    task: activeHelpView.task,
    route: currentPath,
  };

  async function requestHelp(question, historyItems, responseLanguage) {
    const response = await fetchWithSession("/api/damprojects/paxbot/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question,
        history: historyItems.slice(-4),
        context: { ...helpContext, responseLanguage },
      }),
    });
    const result = await response.json();
    if (!response.ok || result.status === "error") {
      throw new Error(result.message || "Pax AI request failed");
    }
    return result.answer;
  }

  function logout() {
    session.invalidate();
    if (sessionUpdate) sessionUpdate();
  }

  function toggleNavigation() {
    if (expanded) {
      closingNavigation = true;
      return;
    }

    expanded = true;
  }

  function finishNavigationCollapse() {
    if (!closingNavigation) return;

    expanded = false;
    closingNavigation = false;
  }
</script>

<div class="app-side-nav" class:expanded id="app-side-nav">
  <div class="side-nav-header-area">
    <div class="side-nav-header">
      <div class="side-nav-header-logo">
        {#if session.isValid}
          <Link to={logoHref} aria-label="Open Project Overview">
            {#if expanded && !closingNavigation}
              <span
                class="side-nav-wordmark"
                in:fly={{ x: -6, duration: 140, delay: 60, opacity: 0 }}
                out:fade={{ duration: 80 }}
                on:outroend={finishNavigationCollapse}
              >
                <SideNavHeaderLogo full color="#FF5F1F" />
              </span>
            {:else if !expanded}
              <span
                class="side-nav-mark"
                in:fade={{ duration: 100, delay: 40 }}
                out:fade={{ duration: 100 }}
              >
                <SideNavLogo color="#FF5F1F" />
              </span>
            {/if}
          </Link>
        {:else}
          <!-- Participants without a session would only land on the sign-in page. -->
          <span class="side-nav-mark">
            <SideNavLogo color="#FF5F1F" />
          </span>
        {/if}
      </div>
    </div>
  </div>
  <div class="side-nav-content">
    <div class="side-nav-content__flex">
      {#if session.isValid}
        <AppSideNavItem
          text="Home"
          href={homeHref}
          keepParams={keepHomeParams}
          reload={true}
        >
          <div class="icon_wrapper">
            <img
              class="home_icon"
              src="/img/assistant/icons/home.svg"
              height="20"
              width="20"
              alt="home"
            />
          </div>
        </AppSideNavItem>
        {#if showCompanySettings}
          <CompanySettingsMenu />
        {/if}
      {/if}
      {#if $processRegistrationMatcher && session.isValid}
        {#if !$anonymizationCheckMatcher}
          <div class="divider" />
          <AppSideNavItem
            href={ROUTES.processRegistration.dashboard}
            text="Dashboard"
          >
            <div class="icon_wrapper" style="width: 1rem;">
              <img
                src="/icons/dashboard.svg"
                height="32"
                width="32"
                alt="dashboard"
              />
            </div>
          </AppSideNavItem>
          <AppSideNavItem
            href={ROUTES.processRegistration.overview}
            text="Registration Overview"
          >
            <div class="icon_wrapper" style="width: 1.5rem;">
              <img
                src="/icons/list_menu.svg"
                height="32"
                width="32"
                alt="list_menu"
              />
            </div>
          </AppSideNavItem>
        {/if}
        <!-- <AppSideNavItem
          href={ROUTES.processRegistration.check}
          text="Registration Check"
        >
          <div class="icon_wrapper" style="width: 1.5rem;">
            <img
              src="/icons/list_check.svg"
              height="32"
              width="32"
              alt="list_check"
            />
          </div>
        </AppSideNavItem> -->
        <!-- <AppSideNavItem
          href={ROUTES.processRegistration.taskExplorer}
          text="Task Explorer"
        >
          <div class="icon_wrapper" style="width: 1.5rem;">
            <img
              src="/icons/task_explorer.svg"
              height="20"
              width="20"
              alt="taskExplorer"
            />
          </div>
        </AppSideNavItem>
        <AppSideNavItem
          href={ROUTES.processRegistration.caseIdMapper}
          text="Object Explorer"
        >
          <div class="icon_wrapper" style="width: 1.5rem;">
            <img
              src="/icons/task_explorer.svg"
              height="20"
              width="20"
              alt="caseIdMapper"
            />
          </div>
        </AppSideNavItem>
        <AppSideNavItem
          href={ROUTES.processRegistration.anonymizationCheck}
          text="Anonymization Check"
        >
          <div class="icon_wrapper" style="width: 1.5rem;">
            <img
              src="/icons/privacy.svg"
              height="32"
              width="32"
              alt="anonymizationCheck"
            />
          </div>
        </AppSideNavItem> -->
      {/if}
      {#if $analysisRouteMatcher}
        <div class="divider" />
        <div class="side-nav-section-heading">
          <div class="section-heading-text">
            <div class="section-heading-title">Usage Analysis</div>
          </div>
        </div>
        {#if $uiFeatureStore.analysisViews.appDrilldown}
          <AppSideNavItem href={ROUTES.analysis.treemap} text="App Drilldown">
          <div class="icon_wrapper">
            <img
              src="/icons/treemap.svg"
              height="20"
              width="20"
              alt="treemap"
            />
          </div>
          </AppSideNavItem>
        {/if}
        {#if $uiFeatureStore.analysisViews.appFlow}
          <AppSideNavItem href={ROUTES.analysis.connections} text="App Flow">
          <div class="icon_wrapper">
            <img
              src="/icons/interfaceExplorer.svg"
              height="20"
              width="20"
              alt="connections"
            />
          </div>
          </AppSideNavItem>
        {/if}
        <!-- <AppSideNavItem href={ROUTES.analysis.heatmap} text="Mask Analysis">
          <div class="icon_wrapper">
            <img
              src="/icons/deepMask.svg"
              height="20"
              width="20"
              alt="heatmap"
            />
          </div>
        </AppSideNavItem> -->
        {#if $uiFeatureStore.analysisViews.workloads}
          <AppSideNavItem href={ROUTES.analysis.workload} text="Workloads">
          <div class="icon_wrapper">
            <img
              src="/icons/workload.svg"
              height="20"
              width="20"
              alt="workload"
            />
          </div>
          </AppSideNavItem>
        {/if}
        {#if $uiFeatureStore.analysisViews.shortcutAnalysis}
          <AppSideNavItem
            href={ROUTES.analysis.shortcuts}
            text="Copy-Paste Analysis"
          >
          <div class="icon_wrapper">
            <img
              src="/icons/shortCuts.svg"
              height="20"
              width="20"
              alt="shortcuts"
            />
          </div>
          </AppSideNavItem>
        {/if}
        <div class="divider" />
        <div class="side-nav-section-heading">
          <div class="section-heading-text">
            <div class="section-heading-title">Workflow Analysis</div>
          </div>
        </div>
        {#if $uiFeatureStore.analysisViews.processExplorer}
          <AppSideNavItem href={ROUTES.analysis.workflow} text="Process Explorer">
          <div class="icon_wrapper">
            <span class="graph_2_icon" aria-hidden="true"></span>
          </div>
          </AppSideNavItem>
        {/if}
        {#if $uiFeatureStore.analysisViews.caseExplorer}
          <AppSideNavItem
            href={ROUTES.analysis.caseExplorer}
            text="Object Explorer"
          >
            <div class="icon_wrapper">
              <span class="object_track_icon" aria-hidden="true"></span>
            </div>
          </AppSideNavItem>
        {/if}
        {#if $uiFeatureStore.analysisViews.kpiMatrix}
          <AppSideNavItem href={ROUTES.analysis.kpiMatrix} text="KPI Matrix">
          <div class="icon_wrapper">
            <img src="/icons/KPIs.svg" height="20" width="20" alt="kpiMatrix" />
          </div>
          </AppSideNavItem>
        {/if}
        {#if $uiFeatureStore.analysisViews.changeAnalysis}
          <AppSideNavItem href="" text="Change Analysis" preventNavigation>
          <div class="icon_wrapper">
            <img
              src="/icons/showChart.svg"
              height="20"
              width="20"
              alt="changeAnalysis"
            />
          </div>
          </AppSideNavItem>
        {/if}
      {/if}
      {#if $damAnalysisRouteMatcher}
        <div class="divider" />
        <div class="side-nav-section-heading">
          <div class="section-heading-text">
            <div class="section-heading-title">Usage Analysis</div>
          </div>
        </div>
        {#if $uiFeatureStore.analysisViews.appDrilldown}
          <AppSideNavItem href={ROUTES.dam.analysis.treemap} text="App Drilldown">
          <div class="icon_wrapper">
            <img
              src="/icons/treemap.svg"
              height="20"
              width="20"
              alt="treemap"
            />
          </div>
          </AppSideNavItem>
        {/if}
        {#if $uiFeatureStore.analysisViews.workloads}
          <AppSideNavItem href={ROUTES.dam.analysis.workload} text="App Users">
          <div class="icon_wrapper">
            <img
              src="/icons/appUsers.svg"
              height="20"
              width="20"
              alt="app users"
            />
          </div>
          </AppSideNavItem>
        {/if}
        {#if $uiFeatureStore.analysisViews.appFlow}
          <AppSideNavItem href={ROUTES.dam.analysis.connections} text="App Flow">
          <div class="icon_wrapper">
            <img
              src="/icons/appFlow.svg"
              height="20"
              width="20"
              alt="app flow"
            />
          </div>
          </AppSideNavItem>
        {/if}
        <!-- <AppSideNavItem href={ROUTES.dam.analysis.heatmap} text="Mask Analysis">
          <div class="icon_wrapper">
            <img
              src="/icons/deepMask.svg"
              height="20"
              width="20"
              alt="heatmap"
            />
          </div>
        </AppSideNavItem> -->
        {#if $uiFeatureStore.analysisViews.shortcutAnalysis}
          <AppSideNavItem
            href={ROUTES.dam.analysis.shortcuts}
            text="Copy & Paste"
          >
          <div class="icon_wrapper">
            <img
              src="/icons/copyAndPaste.svg"
              height="20"
              width="20"
              alt="copy and paste"
            />
          </div>
          </AppSideNavItem>
        {/if}
        <div class="divider" />
        <div class="side-nav-section-heading">
          <div class="section-heading-text">
            <div class="section-heading-title">Workflow Analysis</div>
          </div>
        </div>
        {#if $uiFeatureStore.analysisViews.processExplorer}
          <AppSideNavItem href={ROUTES.dam.analysis.workflow} text="Process Explorer">
          <div class="icon_wrapper">
            <span class="graph_2_icon" aria-hidden="true"></span>
          </div>
          </AppSideNavItem>
        {/if}
        {#if $uiFeatureStore.analysisViews.caseExplorer}
          <AppSideNavItem
            href={ROUTES.dam.analysis.caseExplorer}
            text="Object Explorer"
          >
            <div class="icon_wrapper">
              <span class="object_track_icon" aria-hidden="true"></span>
            </div>
          </AppSideNavItem>
        {/if}
        {#if $uiFeatureStore.analysisViews.kpiMatrix}
          <AppSideNavItem href={ROUTES.dam.analysis.kpiMatrix} text="KPI Matrix">
          <div class="icon_wrapper">
            <img src="/icons/KPIs.svg" height="20" width="20" alt="kpiMatrix" />
          </div>
          </AppSideNavItem>
        {/if}
        {#if $uiFeatureStore.analysisViews.changeAnalysis}
          <AppSideNavItem href={ROUTES.dam.analysis.changeAnalysis} text="Change Analysis">
          <div class="icon_wrapper">
            <img
              src="/img/assistant/icons/compare_arrows_dark.svg"
              height="20"
              width="20"
              alt="changeAnalysis"
            />
          </div>
          </AppSideNavItem>
        {/if}
      {/if}
      {#if $monitoringRouteMatcher && !$processMonitoringRouteMatcher}
        <div class="divider" />
        <div class="side-nav-section-heading">
          <div class="section-heading-text">
            <div class="section-heading-title">Software Intelligence</div>
          </div>
        </div>
        <AppSideNavItem
          href={ROUTES.monitoring.root}
          text="Software Portfolio"
          keepParams={false}
        >
          <div class="icon_wrapper">
            <img
              src="/icons/dashboard.svg"
              height="20"
              width="20"
              alt="overview"
            />
          </div>
        </AppSideNavItem>
        <AppSideNavItem
          href={ROUTES.monitoring.catalog}
          text="Software Catalog"
          keepParams={false}
        >
          <List16 />
        </AppSideNavItem>
        {#if $uiFeatureStore.platformFeatures.licenseCatalog}
          <AppSideNavItem
            href={ROUTES.monitoring.licenseCatalog}
            text="License Catalog"
            keepParams={false}
          >
            <div class="icon_wrapper">
              <img src="/icons/license.svg" height="20" width="20" alt="license" />
            </div>
          </AppSideNavItem>
        {/if}
        {#if $uiFeatureStore.analysisViews.changeAnalysis}
          <AppSideNavItem
            href={ROUTES.monitoring.changeAnalysis}
            text="Software Change Analysis"
            keepParams={false}
          >
            <ChartLine16 />
          </AppSideNavItem>
        {/if}
        <AppSideNavItem
          href={ROUTES.monitoring.categoryDeepDive}
          text="Category Deep Dive"
          keepParams={false}
        >
          <div class="icon_wrapper">
            <img
              src="/icons/treemap.svg"
              height="20"
              width="20"
              alt="treemap"
            />
          </div>
        </AppSideNavItem>
      {/if}
      {#if $processMonitoringRouteMatcher}
        <div class="divider" />
        <div class="side-nav-section-heading">
          <div class="section-heading-text">
            <div class="section-heading-title">Process Intelligence</div>
          </div>
        </div>
        <AppSideNavItem
          href={ROUTES.monitoring.processRoot}
          text="Process Portfolio"
          keepParams={false}
        >
          <div class="icon_wrapper">
            <img
              src="/icons/dashboard.svg"
              height="20"
              width="20"
              alt="overview"
            />
          </div>
        </AppSideNavItem>
        <AppSideNavItem
          href={ROUTES.monitoring.processCatalog}
          text="Process Catalog"
          keepParams={false}
        >
          <List16 />
        </AppSideNavItem>
        {#if $uiFeatureStore.analysisViews.changeAnalysis}
          <AppSideNavItem
            href={ROUTES.monitoring.processChangeAnalysis}
            text="Change Analysis"
            keepParams={false}
          >
            <ChartLine16 />
          </AppSideNavItem>
        {/if}
        <AppSideNavItem
          href={ROUTES.monitoring.processDeepDive}
          text="Process Deep Dive"
          keepParams={false}
        >
          <div class="icon_wrapper">
            <img
              src="/icons/treemap.svg"
              height="20"
              width="20"
              alt="treemap"
            />
          </div>
        </AppSideNavItem>
      {/if}
      {#if $preprocessingMatcher}
        <div class="divider" />
        <AppSideNavItem
          href={ROUTES.preprocessing.appPreprocessing}
          text="App Preprocessing"
          ><Settings16 />
        </AppSideNavItem>

        <AppSideNavItem
          href={ROUTES.preprocessing.uiPreprocessingText}
          text="UI Element Preprocessing"
          ><Settings16 />
        </AppSideNavItem>

        <AppSideNavItem
          href={ROUTES.preprocessing.uiPreprocessingScreen}
          text="Screenshot Preprocessing"
          ><Settings16 />
        </AppSideNavItem>

        <AppSideNavItem
          href={ROUTES.preprocessing.preprocessingSummary}
          text="Preprocessing Summary"
          ><Settings16 />
        </AppSideNavItem>

        <AppSideNavItem
          href={ROUTES.dam.mappingAssistant}
          text="Mapping Assistant"
          ><Settings16 />
        </AppSideNavItem>

        <AppSideNavItem
          href={ROUTES.preprocessing.overviewPreprocessing}
          text="Overview Preprocessing"
          ><Settings16 />
        </AppSideNavItem>
      {/if}
      {#if $knexRouteMatcher}
        {#if !$potentialScopeMatcher && !$surveyRouteMatcher}
        <AppSideNavItem
          href={ROUTES.knex.root}
          text="Overview"
          keepParams={false}
        >
          <Folder16 />
        </AppSideNavItem>
        <AppSideNavItem
          href={ROUTES.knex.presets}
          text="Presets"
          keepParams={false}
        >
          <List16 />
        </AppSideNavItem>
        <AppSideNavItem
          href={ROUTES.knex.userList}
          text="User List"
          keepParams={false}
        >
          <UserIcon />
        </AppSideNavItem>
        {/if}
        {#if $potentialScopeMatcher && !$potentialDashboardMatcher && !$manageInvitationsMatcher}
          <div class="divider" />
          <AppSideNavItem
            href={ROUTES.knex.potentialScope.taskOverview}
            text="Task Overview"
          >
            <div class="icon_wrapper">
              <img
                src="/icons/dashboard.svg"
                height="20"
                width="20"
                alt="survey overview"
              />
            </div>
          </AppSideNavItem>
          <AppSideNavItem
            href={ROUTES.knex.potentialScope.surveyDetail}
            text="Survey Detail"
          >
            <div class="icon_wrapper">
              <img
                src="/icons/list.svg"
                height="20"
                width="20"
                alt="survey detail"
              />
            </div>
          </AppSideNavItem>
          <!-- <AppSideNavItem href={ROUTES.knex.potentialScope.suggestionBox} text="Suggestion Box">
            <div class="icon_wrapper">
              <img
                src="/icons/suggestion.svg"
                height="20"
                width="20"
                alt="suggestion box"
              />
            </div>
          </AppSideNavItem> -->
        {/if}
      {/if}
      {#if $settingsRouteMatcher}
        <div class="divider" />
        <!-- <AppSideNavItem href="/app/settings/" text="Settings">
          <SettingsIcon />
        </AppSideNavItem>
        <div class="divider" /> -->
        {#if permissions.includes("manage_users")}
          <AppSideNavItem
            text="Users"
            href={ROUTES.settings.userManagement}
            keepParams={false}
          >
            <UserIcon />
          </AppSideNavItem>
        {/if}
        {#if permissions.includes("manage_groups")}
          <AppSideNavItem
            text="Permissions"
            href={ROUTES.settings.permissionManagement}
            keepParams={false}
          >
            <PermissionsIcon />
          </AppSideNavItem>
        {/if}
        {#if permissions.includes("manage_teams")}
          <AppSideNavItem
            text="Teams"
            href={ROUTES.settings.teamManagement}
            keepParams={false}
          >
            <TeamIcon />
          </AppSideNavItem>
        {/if}
        {#if permissions.includes("manage_processes")}
          <AppSideNavItem
            text="Data"
            href={ROUTES.settings.dataManagement}
            keepParams={false}
          >
            <!-- <svelte:component this={DataIcon} /> -->
            <DataIcon />
          </AppSideNavItem>
        {/if}
      {/if}
    </div>
  </div>

  <div class="side-nav-footer">
    <div class="side-nav-content__flex">
      <!-- @Adrian Language-Item -->
      <!-- <AppSideNavItem text="Language">
        <Language />
      </AppSideNavItem> -->

      <!-- Das geteilte Registration Dashboard wird oft ohne Login geöffnet, die Tour gibt es dort trotzdem. -->
      {#if (session.isValid || currentPath === ROUTES.processRegistration.dashboard) && $viewTour && $uiFeatureStore.platformFeatures.guidedTours}
        <button
          class="side-nav-help"
          type="button"
          aria-label="Start tour for the current view"
          data-tooltip="Start tour"
          on:click={() => {
            paxbotOpen = false;
            $viewTour.start();
          }}
        >
          <img class="side-nav-help-icon" src="/img/assistant/icons/explore.svg" alt="" />
          <span class="side-nav-help-text">Tour</span>
        </button>
      {/if}
      {#if session.isValid}
        {#if activeHelpView.enabled && !(currentPath === ROUTES.dam.appnamemapping && $viewTour && $uiFeatureStore.platformFeatures.guidedTours)}
          <button
            class="side-nav-help"
            type="button"
            aria-label="Open help for the current view"
            title="Help for the current view"
            on:click={() => (paxbotOpen = !paxbotOpen)}
          >
            <img
              class="side-nav-help-icon"
              src="/img/assistant/icons/help_questionmark.svg"
              alt=""
            />
            <span class="side-nav-help-text">Help</span>
          </button>
        {/if}
        <AccountMenu
          isAdmin={$role?.role === "admin"}
          username={accountName}
          email={accountEmail}
          onLogout={logout}
        />
      {/if}
      <div class="divider" />
      <button
        class="side-nav-toggle"
        type="button"
        aria-label={expanded ? "Collapse navigation" : "Expand navigation"}
        data-tooltip="Expand navigation"
        on:click={toggleNavigation}
      >
        <img
          class="side-nav-toggle-icon"
          src={expanded
            ? "/img/assistant/icons/chevron_left_dark.svg"
            : "/img/assistant/icons/chevron_right_dark.svg"}
          alt=""
        />
        <span class="side-nav-toggle-text">Collapse navigation</span>
      </button>
    </div>
  </div>
</div>

<PaxbotChat
  bind:open={paxbotOpen}
  heading="Pax AI"
  subheading={activeHelpView.title}
  contextTitle="Current view"
  contextItems={[{ title: activeHelpView.title, description: activeHelpView.task }]}
  requestAnswer={requestHelp}
  starterQuestions={activeHelpView.starterQuestions}
  bottomOffset="1rem"
  showMascot={false}
/>

<style>
  .app-side-nav {
    height: 100%;
    width: var(--app-side-nav-width);
    box-sizing: border-box;
    display: flex;
    padding: 0 0.55rem;
    flex-direction: column;
    align-items: center;
    background-color: var(--pax-white);
    border-right: var(--bw) solid var(--border-color);
    will-change: width;
    transition: width 0.2s ease;
  }

  .app-side-nav.expanded {
    width: var(--app-side-nav-expanded-width);
  }

  :global(.app-side-nav.expanded .side-nav-item-title) {
    display: flex;
    gap: 1rem;
  }
  :global(.app-side-nav.expanded .side-nav-item) {
    width: 100%;
    justify-content: flex-start;
  }
  .app-side-nav.expanded .side-nav-content {
    /* Nav-Breite (Appshell: --app-side-nav-expanded-width) minus Innenabstand */
    width: calc(var(--app-side-nav-expanded-width) - 2rem);
  }
  .app-side-nav.expanded .side-nav-content__flex {
    align-items: flex-start; /** Resets the ui, such that the icons are aligned.*/
  }
  .app-side-nav.expanded .side-nav-footer {
    align-items: flex-start; /** Resets the ui, such that the icons are aligned.*/
  }
  .app-side-nav.expanded .side-nav-footer > .side-nav-content__flex {
    width: calc(var(--app-side-nav-expanded-width) - 2rem);
  }
  .app-side-nav.expanded .section-heading-text {
    display: block;
  }

  .app-side-nav.expanded .side-nav-section-heading {
    display: flex;
  }

  .app-side-nav.expanded .side-nav-header {
    align-items: center;
  }

  .app-side-nav.expanded .side-nav-header-logo {
    width: 100%;
    border-right: 0;
  }

  .side-nav-header-area {
    width: calc(100% + 1.1rem);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    height: var(--app-header-height);
    min-height: var(--app-header-height);
    margin: 0 -0.55rem;
    box-sizing: border-box;
  }

  .divider {
    flex: 0 0 auto;
    height: 0;
    width: 100%;
    border-top: 1px solid #c9c9c9;
    background: transparent;
    margin: 0;
    box-sizing: border-box;
  }

  .side-nav-header {
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }

  .side-nav-header-logo {
    display: flex;
    width: var(--app-header-height);
    height: var(--app-header-height);
    box-sizing: border-box;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: none;
    overflow: hidden;
    background: var(--pax-white);
    border-right: var(--bw) solid var(--border-color);
    border-bottom: var(--bw) solid var(--border-color);
  }

  .side-nav-header-logo :global(a) {
    position: relative;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    cursor: pointer;
  }

  .side-nav-mark {
    display: flex;
    position: absolute;
    left: 0;
    width: var(--app-header-height);
    height: var(--app-header-height);
    justify-content: center;
    align-items: center;
  }

  .side-nav-wordmark {
    display: flex;
    width: 4.25rem;
    height: auto;
  }

  .side-nav-header-logo :global(svg) {
    width: 1.625rem;
    height: auto;
  }

  .side-nav-content {
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .side-nav-content__flex {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
  }

  .side-nav-content > .side-nav-content__flex {
    padding-top: 0.5rem;
  }

  .app-side-nav:not(.expanded) .side-nav-content__flex {
    align-items: center;
  }

  .side-nav-footer {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: auto;
    margin-bottom: 1rem;
  }

  .side-nav-footer > .side-nav-content__flex {
    width: 100%;
  }

  .icon_wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
  }

  .home_icon {
    filter: brightness(0);
  }

  .graph_2_icon {
    background: var(--pax-black);
    display: inline-block;
    height: 20px;
    mask: url("/img/assistant/icons/graph_2.svg") center / contain no-repeat;
    -webkit-mask: url("/img/assistant/icons/graph_2.svg") center / contain no-repeat;
    width: 20px;
  }

  .object_track_icon {
    background: var(--pax-black);
    display: inline-block;
    height: 20px;
    mask: url("/img/assistant/icons/object_track.svg") center / contain no-repeat;
    -webkit-mask: url("/img/assistant/icons/object_track.svg") center / contain no-repeat;
    width: 20px;
  }

  .side-nav-section-heading {
    display: none;
    align-items: center;
    padding: 0.25rem 0;
  }

  .section-heading-text {
    display: none;
    min-width: calc(var(--app-side-nav-expanded-width) - 3.85rem);
  }

  .section-heading-title {
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.2rem;
  }

  .side-nav-toggle {
    position: relative;
    width: 2rem;
    height: 2rem;
    border: none;
    border-radius: var(--br);
    background: var(--pax-white);
    color: var(--pax-black);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    cursor: pointer;
    font-family: var(--font-family);
  }

  .side-nav-toggle:hover {
    background: var(--pax-smoke);
  }

  .side-nav-help {
    position: relative;
    width: 100%;
    min-height: 2rem;
    border: 0;
    border-radius: var(--br);
    background: transparent;
    color: var(--pax-black);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    cursor: pointer;
    font-family: var(--font-family);
  }

  .side-nav-help:hover {
    background: var(--pax-smoke);
  }

  .app-side-nav:not(.expanded) .side-nav-help[data-tooltip]:is(:hover, :focus-visible)::after,
  .app-side-nav:not(.expanded) .side-nav-toggle[data-tooltip]:is(:hover, :focus-visible)::after {
    content: attr(data-tooltip);
    position: absolute;
    left: calc(100% + 0.75rem);
    top: 50%;
    z-index: var(--z100);
    transform: translateY(-50%);
    border-radius: 0.25rem;
    padding: 0.4rem 0.55rem;
    background: var(--pax-black);
    color: var(--pax-white);
    font-size: 0.75rem;
    line-height: 1rem;
    white-space: nowrap;
    pointer-events: none;
  }

  .app-side-nav:not(.expanded) .side-nav-help[data-tooltip]:is(:hover, :focus-visible)::before,
  .app-side-nav:not(.expanded) .side-nav-toggle[data-tooltip]:is(:hover, :focus-visible)::before {
    content: "";
    position: absolute;
    left: calc(100% + 0.4rem);
    top: 50%;
    z-index: var(--z100);
    transform: translateY(-50%);
    border: 0.35rem solid transparent;
    border-right-color: var(--pax-black);
    pointer-events: none;
  }

  .side-nav-help-icon {
    width: 1.5rem;
    height: 1.5rem;
    filter: brightness(0);
  }

  .side-nav-help-text {
    display: none;
    font-size: 0.75rem;
    font-weight: 300;
  }

  .app-side-nav.expanded .side-nav-help {
    justify-content: flex-start;
    padding: 0 0.4rem;
  }

  .app-side-nav.expanded .side-nav-help-text {
    display: block;
  }

  .app-side-nav.expanded .side-nav-toggle {
    width: 100%;
    justify-content: flex-start;
    padding: 0 0.5rem;
  }

  .side-nav-toggle-icon {
    width: 1rem;
    height: 1rem;
  }

  .side-nav-toggle-text {
    display: none;
    font-size: 0.75rem;
    font-weight: 300;
    line-height: 0.8rem;
  }

  .app-side-nav.expanded .side-nav-toggle-text {
    display: block;
  }
</style>
